import dialogManager from '../components/DialogManager/DialogManager';
import { Select, useTheme, useColorMode, useToast, Button, Box, Text, SimpleGrid, Input } from '@chakra-ui/react';

const HOST = window.location.hostname.includes('a-dec') ? process.env.REACT_APP_ADEC_API_ENDPOINT : process.env.REACT_APP_API_ENDPOINT
const CQAPI_MAX_RETRIES = 5;
const CQAPI_RETRY_BASE_DELAY_MS = 1000; // 0.5s base delay

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getParam = (p)=>{
  const newSearchParams = new URLSearchParams(window.location.search);
  return newSearchParams.get(p);
}

const CQAPI_FETCH_TIMEOUT_MS = process.env.REACT_APP_CQAPI_FETCH_TIMEOUT || 10000;

// A blob URL is a document on the app's OWN origin, so an active content type here
// is a stored-XSS primitive: /file serves back whatever type the upload declared,
// and it will even take one off the query string. Nothing downloaded through this
// path is ever meant to be rendered as markup — these are files people save, audio
// they play, and PDFs they preview — so anything that could execute is handed over
// as an opaque download instead of becoming a live document.
const ACTIVE_BLOB_TYPES = /^(?:text\/html|text\/xml|image\/svg\+xml|application\/(?:xml|xhtml\+xml))$/i;

const inertBlob = (blob) => {
  const type = String(blob.type || '').split(';')[0].trim();
  return ACTIVE_BLOB_TYPES.test(type) ? blob.slice(0, blob.size, 'application/octet-stream') : blob;
};

// Fetch a URL and read its JSON under ONE deadline.
//
// The deadline has to cover the body, not just the headers. Aborting only until the
// response resolves left `res.json()` unbounded, and a server or proxy that sends
// headers and then stalls mid-body hangs the caller for good — no timeout, no catch
// block, nothing to release whatever the request disabled. The controller stays armed
// until the body is parsed.
const fetchJsonWithTimeout = async (url, options = {}, timeout) => {
  const controller = new AbortController();
  let timer = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    // Headers are here; the BODY gets a fresh deadline of the same length. One shared
    // deadline made a slow-but-flowing body pay for its own connect time: an existing
    // GET that used to succeed just past the wire timeout began aborting at exactly
    // `timeout` total. Two phases keep the guarantee that matters — a stalled body can
    // never hang the caller for good — without billing the connect against the read.
    clearTimeout(timer);
    timer = setTimeout(() => controller.abort(), timeout);
    let json;
    try {
      json = await response.json();
    } catch (parseError) {
      // A body cut off by our own deadline is a transport failure, not a server that
      // sent malformed JSON. Told apart, because the retry loop treats them
      // differently: a parse error on a 200 is final, while a deadline is exactly the
      // kind of failure a retry exists for.
      if (controller.signal.aborted) throw parseError;
      json = { type: 'error', msg: 'Unable to parse response' };
    }
    return { response, json };
  } finally {
    clearTimeout(timer);
  }
};


const getUid = () => {
  const queryUid = getParam("_uid");
  if (queryUid) return queryUid;
  if (localStorage.getItem('user')){
    try {
      return JSON.parse(localStorage.getItem('user')).id;
    }
    catch (error) {
      console.log(error);
      return null;
    }
  }
  return null;
};

const getBid = ()=>{
  const queryBid = getParam('bid')
  if (queryBid) return queryBid;
  if (localStorage.getItem('business') && localStorage.getItem('user') && Auth?.user?.isPatient) {
    try {
      return JSON.parse(localStorage.getItem('business')).id;
    }
    catch (error){
      console.log(error);
      return null;
    }
  }
  return null;
};

import React, {useState} from 'react';
import {useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement, Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import AgreementModal from "../components/DialogManager/AgreementModal";
import theme from '../theme';

// Your Stripe public key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PKEY);

const useOptions = () => {
  const theme = useTheme(); // Access the theme context
  const {
    colorMode
  } = useColorMode();

  return {
    style: {
      base: {
        color: colorMode === "light" ? theme.colors.gray[700] : theme.colors.gray[50],
        letterSpacing: "0.025em",
        backgroundColor: colorMode === "light" ? theme.colors.gray[200] : theme.colors.gray[600],
        lineHeight: '40px',
        fontSize: theme.fontSizes.md, // Use font size from the Chakra theme
        fontFamily: theme.fonts.body, // Use the font family from the Chakra theme
        '::placeholder': {
          color: theme.colors.gray[400], // Use a color from the Chakra theme for placeholders
        },
      },
      invalid: {
        color: theme.colors.red[500], // Use a color from the Chakra theme for invalid input
      },
    }
  };
};

const StripeElementsForm = ({onSuccess, onError, _uid, skipSave, bid}) => {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const theme = useTheme();
  const { colorMode } = useColorMode();
  const [zip, setZip] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe has not initialized yet
      return;
    }
    
    const cardElement = elements.getElement(CardNumberElement);
    
    const {error, paymentMethod} = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        address: { postal_code: zip },
      },
    });

    if (error) {
      console.error(error);
      onError(error);
    } else {
      if (skipSave){
        onSuccess(paymentMethod);
        return
      }
      // Here, send the paymentMethod.id to your server
      try {
        const params = new URLSearchParams({
          ...(_uid ? {_uid} : {}), 
          ...(bid ? {bid} : {})
        });
        const response = await cqAPI.post(
          _uid ? `/setting/card?${params.toString()}` : `/setting/subscriptions/card?${params.toString()}`, 
          {
            paymentMethodId: paymentMethod.id
          }
        );

        if (response.type === 'ok') {
          onSuccess(paymentMethod);
        } else {
          toast({
            title: "Error",
            description: response.msg,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
        }
      } catch (error) {
        onError(error);
      }
    }
  };

  const options = useOptions();
  const cellOptions = {
    ...options,
    style: {
      ...options.style,
      base: {
        ...options.style.base,
        backgroundColor: 'transparent',
        lineHeight: '24px',
      },
    },
  };

  const borderColor = colorMode === 'light' ? theme.colors.gray[300] : theme.colors.gray[600];

  const zipInputProps = {
    variant: 'unstyled',
    placeholder: 'ZIP',
    inputMode: 'numeric',
    autoComplete: 'postal-code',
    p: 0,
    h: '24px',
    lineHeight: '24px',
    fontSize: 'md',
    fontFamily: theme.fonts.body,
    color: colorMode === 'light' ? theme.colors.gray[700] : theme.colors.gray[50],
    letterSpacing: '0.025em',
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
    _placeholder: { color: theme.colors.gray[400], letterSpacing: '0.025em' },
    _focus: { boxShadow: 'none', outline: 'none', border: 'none' },
    _focusVisible: { boxShadow: 'none', outline: 'none', border: 'none' },
  };

  return (
    <form onSubmit={handleSubmit}>
      <Text fontWeight="semibold" mb={2}>Card information</Text>
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" overflow="hidden">
        <Box px={4} py={3} borderBottomWidth="1px" borderColor={borderColor}>
          <CardNumberElement
            options={{
              ...cellOptions,
              showIcon: true,
              placeholder: '1234 1234 1234 1234',
            }}
          />
        </Box>
        <SimpleGrid columns={3}>
          <Box px={4} py={3} borderRightWidth="1px" borderColor={borderColor}>
            <CardExpiryElement options={{ ...cellOptions, placeholder: 'MM / YY' }} />
          </Box>
          <Box px={4} py={3} borderRightWidth="1px" borderColor={borderColor}>
            <CardCvcElement options={{ ...cellOptions, placeholder: 'CVC' }} />
          </Box>
          <Box px={4} py={3}>
            <Input
              {...zipInputProps}
              value={zip}
              onChange={(e) => setZip(e.target.value)}
            />
          </Box>
        </SimpleGrid>
      </Box>

      <Button type="submit" marginTop="40px" disabled={!stripe}>Save Card</Button>
    </form>
  );
};

class STREAMAPI{
  init(user, setUser){
    this.user = user;
    this.setUser = setUser
  }
  post(ep, body, options = {}){
    const bid = getBid()
    const lambdaUrl = bid? `${HOST}${ep}?bid=${bid}` : `${HOST}${ep}`
    return fetch(lambdaUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.user && this.user.token ? {
          Authorization : `Bearer ${this.user.token}`
        } : {}
      },
      body: JSON.stringify(body || {}),
      ...options.signal ? { signal: options.signal } : {}
    });
  }
}

class CQAPI {
  init(user, setUser) {
    this.user = user;
    this.setUser = setUser
  }
  async performRequest(method, endpoint, body, timeout, options = {}) {
    const requestOptions = {
      headers: {
        ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(this.user && this.user.token ? { Authorization: `Bearer ${this.user.token}` } : {})
      },
      method,
      withCredentials: true,
      ...(body
        ? body instanceof FormData
          ? { body }
          : { body: JSON.stringify(body) }
        : {}
      )
    };

    let lastError = null;

    // How many times this request may be SENT. Retrying is safe for a read, and for a
    // write the server can recognise as a replay — the support console's sends carry a
    // client_message_id for exactly that. It is not safe for a write that simply
    // overwrites: a slow-but-committed request, aborted at its deadline and sent again,
    // re-applies an intent that a later decision has already superseded. Such callers
    // ask for a single attempt.
    const maxAttempts = Math.max(1, Math.min(options.attempts || CQAPI_MAX_RETRIES, CQAPI_MAX_RETRIES));

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        let response;
        let jsonResponse;

        // GETs get a timeout by default; writes get one only when the CALLER passes it.
        // A write with no bound can hang on a half-open connection forever — its
        // `finally` never runs and every control it disabled stays disabled — but a
        // blanket write timeout is not safe either (uploads legitimately run long, and a
        // timed-out POST is retried by this loop, which only idempotent callers can
        // afford). So the default stays bare fetch, and a caller that both needs a bound
        // and can tolerate a retry — or asks for a single attempt — opts in.
        if (timeout) {
          ({ response, json: jsonResponse } =
            await fetchJsonWithTimeout(`${HOST}${endpoint}`, requestOptions, timeout));
        } else {
          response = await fetch(`${HOST}${endpoint}`, requestOptions);
          try {
            jsonResponse = await response.json();
          } catch (parseError) {
            jsonResponse = { type: 'error', msg: 'Unable to parse response' };
          }
        }

        // If the response is OK or looks like a normal API error, just return it
        if (response.ok) {
          return jsonResponse;
        }

        // For non-OK HTTP status codes:
        // Retry only on 5xx (server) errors; return immediately on 4xx
        if (response.status >= 500 && response.status < 600) {
          lastError = new Error(
            `Server error ${response.status} on attempt ${attempt + 1}`
          );
        } else {
          return jsonResponse || {
            type: 'error',
            msg: `Request failed with status ${response.status}`,
            code: response.status
          };
        }
      } catch (error) {
        // Network / fetch-level error (what we expect from "spotty internet")
        lastError = error;
      }

      // If we're here, something failed. If we have retries left, back off then try again.
      if (attempt < maxAttempts - 1) {
        const delay = CQAPI_RETRY_BASE_DELAY_MS * Math.pow(2, attempt); // 0.5s, 1s, 2s, 4s, ...
        await sleep(delay);
      }
    }

    // All retries exhausted
    return {
      type: 'error',
      msg: 'Network Error - Please try again later!'
    };
  }

  getAPIURL(url) {
    return `${HOST}${url}`;
  }
  
    // `timeoutMs` is OPTIONAL and off by default, so every existing caller keeps exactly
    // the behaviour it had. A caller that asks for one gets a deadline over the headers
    // AND the body: a response that starts and then stalls mid-body leaves this promise
    // unsettled forever, and a caller waiting on it has no way to give up — its spinner
    // never clears and clicking again just starts another request beside the first.
    async downloadFile(ep, { timeoutMs } = {}){
      const controller = timeoutMs ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
      const requestOptions = {
        headers: {
          ...this.user && this.user.token ? { Authorization: `Bearer ${this.user.token}` } : {}
        },
        withCredentials: true,
        method: 'GET',
        ...(controller ? { signal: controller.signal } : {})
      };
      try {
        const endpoint = this.getEndpoint(ep)
        const response = await fetch(this.getAPIURL(endpoint),requestOptions)
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const blob = await response.blob()
        return window.URL.createObjectURL(inertBlob(blob))
      } catch (error) {
        console.log(`Download error: ${JSON.stringify(error, null, 2)}`)
        return error.response; // Handle the error response
      } finally {
        if (timer) clearTimeout(timer);
      }
    }

    async requestCreditCard({endpoint, skipSave, uid, bid}) {
      return new Promise((resolve, reject) => {
        const onSuccess = (response) => {
          dialogManager.close();
          resolve(response);
        };

        const _uid = uid || (endpoint && endpoint.includes('_uid=') && endpoint.split('_uid=')[1].split('&')[0]);
        //if _uid is passed, it means we are updating the user not the business.

        const onError = (error) => {
          console.error("Error saving card:", error);
          reject(error);
        };

        const formWithProvider = (
          <Elements stripe={stripePromise}>
            <StripeElementsForm 
              onSuccess={onSuccess} 
              onError={onError} 
              _uid={_uid} 
              bid={bid} 
              skipSave={skipSave}
            />
          </Elements>
        );

        // Open the dialog
        dialogManager.open({
          body: formWithProvider,
          isClosable: true,
          onDeny: () => {
            resolve(null);
          }
        });
      });
    }

  async requestAgreement(data, callback) {
    return new Promise((resolve, reject) => {
      const onSuccess = async () => {
        dialogManager.close();
        try {
          const resp = await callback();
          return resolve(resp);
        } catch (e) {
          console.log(e)
        }
      };

      const onError = (error) => {
        console.error("Error accepting agreement:", error);
        reject(error);
      };

      // Open the agreement modal in the dialog manager
      dialogManager.open({
        body: <AgreementModal agreements={data} onDone={onSuccess} onError={onError}/>,
        isClosable: false,
        theme
      });
    });
  }


    // `timeout` and `options` travel with the retries below. Every branch here replays
    // the caller's request, and a replay that drops them is a different request: an
    // unbounded one, and one the transport may repeat up to five times. The support
    // console and widget send status changes, AI pauses and escalations with a deadline
    // and a single attempt precisely because repeating them undoes someone else's
    // decision or pages a team twice.
    async errorHandler(response, method, endpoint, body, timeout, options) {
      if (response.type === 'ok' &&  response.redir) {
          window.location.href = response.redir;
      }

      if (response && response.type == 'error') {
        // Handle specific error codes as needed
        if (response.code == 100) {
          this.setUser(null);
          // A WRITE is answered, not parked. The queue below never settles — `onLogin`
          // stores the callback and `Auth.login` awaits `Promise.all` over an array of
          // FUNCTIONS, which resolves without calling any of them — so a parked write
          // hangs for the life of the tab: no catch, no finally, and every control it
          // disabled stays disabled with the user's words inside it. The server has in
          // fact refused this request and stored nothing, which is exactly what the
          // caller needs to be told. Reads keep the existing behaviour: replaying a GET
          // after login is harmless, and that is the path the app has always taken.
          if (method !== 'get') return response;
          return new Promise((resolve) => {
            const loginResolver = () => {
              resolve(this[method](endpoint, body, timeout, options));
            };
            Auth.onLogin(loginResolver);
          });
        }
        if (response.code == 6111) {
          const shouldRetry = await this.showRetryDialog(response.msg);
          if (shouldRetry) {
            return this[method](endpoint + (endpoint.includes('?')?'&':'?') +'paidcash=1', body, timeout, options); // Retry the request
          }else{
            return this[method](endpoint + (endpoint.includes('?')?'&':'?') +'paidcash=0', body, timeout, options); // Retry the request
          }
        }

        if (response.code == 1100) {
          const msg = response.full_msg? response.msg : response.msg + ' Are you sure you want to continue?';
          const shouldRetry = await this.showRetryDialog(msg, response.header, response.deny_text);
          if (shouldRetry) {
            return this[method](endpoint + (endpoint.includes('?')?'&':'?') +'confirm=1', body, timeout, options); // Retry the request
          }else{
            return false
          }
        }
        if (response.code == 6100){
          const storedBid = localStorage.getItem('bid')
          const l = storedBid || (await LocationSelectorManager.openLocationSelector().then(x=>x.bid))
          if (getBid() != l) {
            const newSearchParams = new URLSearchParams(window.location.search);
            newSearchParams.set("bid", l);
            window.location.replace(`${window.location.pathname}?${newSearchParams.toString()}`);
          }

          return this[method](endpoint, body, timeout, options);
        }
        if (response.code == 6015){
          //get credit card from the user
          try {
            const resp = await this.requestCreditCard({
              ...(response.bid? {bid : response.bid} : {})
            });
            if (!resp){
              return {type: 'error' , 'msg' : 'Please add a credit card to your account.'}
            }
            return this[method](endpoint, body, timeout, options);
          } catch (error) {
            console.error(`Unable to get credit card: ${error}`);
          }
        }
        if (response.code == 1015){
          //get credit card from the user
          try {
            const resp = await this.requestCreditCard({endpoint, ...response.uid? {uid : response.uid} : {}});
            if (!resp){
              return {type: 'error' , 'msg' : 'Please add a credit card to your account.'}
            }
            return this[method](endpoint, body, timeout, options);
          } catch (error) {
            console.error(`Unable to get credit card: ${error}`);
          }
        }
        if (response.code == 7001) {
          try {
            // The callback IS the retry, and its answer is the answer. Re-issuing the
            // request after it resolved sent every agreement-gated write twice — two
            // replies to a customer, two pages to a team from one escalation — and the
            // second went out with no deadline and the transport's full retry budget.
            const resp = await this.requestAgreement(response.data, () => this[method](endpoint, body, timeout, options));
            if (!resp) {
              return { type: 'error', 'msg': 'Something Went Wrong!.' }
            }
            return resp;
          } catch (error) {
            console.error(`Unable to accept agreements: ${error}`);
          }
        }
      }

      return response;
    }


  async showRetryDialog(message, header = "", denyText = 'No') {
        return new Promise((resolve) => {
          dialogManager.open({
            onAccept: () => {resolve(true); return true},
            onDeny: () => resolve(false),
            acceptText: 'Yes',
            denyText,
            body: message,
            header: header || 'Confirmation needed',
            theme
          });
        });
      }

      getEndpoint(ep, urlParams ={}){
        const bid = getBid()
        const _uid = getUid()
        const bids = getParam('bids')
        const shouldAddReportBids =
          !bid
          && bids
          && !ep.includes('bids=')
          && !ep.startsWith('/reports/locations')
          && (/^\/reports(\/|$)/.test(ep) || /^\/audit(\?|$)/.test(ep));
        const requestURLparams = {
          ...urlParams,
          ...(bid && !ep.includes('bid=')? {bid} : {}),
          ...(shouldAddReportBids ? {bids} : {}),
          ...(_uid && !ep.includes('_uid=') ? {_uid} : {})
        };
        const encodedParams = new URLSearchParams(requestURLparams).toString();
        if (!encodedParams) {
          return ep;
        }

        const separator = ep.includes('?')?'&':'?';
        return `${ep}${separator}${encodedParams}`
      }

      async post(ep, body, timeout, options) {
        body = body || {}
        const endpoint = this.getEndpoint(ep);
        const response = await this.performRequest('post', endpoint, body, timeout, options);
        return this.errorHandler(response, 'post', endpoint, body, timeout, options);
      }

      async get(ep, urlParams= {},timeout=CQAPI_FETCH_TIMEOUT_MS) {
        const endpoint = this.getEndpoint(ep, urlParams);
        const response = await this.performRequest('get', endpoint, undefined, timeout);
        return this.errorHandler(response, 'get', endpoint, undefined, timeout);
      }
      // `timeout` and `options` like its siblings. Without them every delete in the app
      // was an unbounded fetch: a server that commits the deletion and then leaves the
      // response half-open never settles the promise, so the caller's `finally` never
      // runs and whatever it disabled stays disabled. Both stay OPTIONAL, so a caller
      // that passes neither keeps exactly the behaviour it had.
      async del(ep, body, timeout, options) {
        const endpoint = this.getEndpoint(ep);
        const response = await this.performRequest('delete', endpoint, body, timeout, options);
        return this.errorHandler(response, 'del', endpoint, body, timeout, options);
      }
      async put(ep, body, timeout, options) {
        const endpoint = this.getEndpoint(ep);
        const response = await this.performRequest('put', endpoint, body, timeout, options);
        return this.errorHandler(response, 'put', endpoint, body, timeout, options);
      }
  }


const cqAPI = new CQAPI()
const streamAPI = new STREAMAPI()

class AUTH {

  init(user, setUser) {
    this.queue = []
    this.user = user;
    this.setUser = setUser
    this.storeUser = (u) => {
      this.user = u;
      this.setUser(u);
      cqAPI.user = u;
      streamAPI.user = u;
      if (u) {
        localStorage.setItem("user", JSON.stringify(u));
      } else {
        localStorage.removeItem("user");
      }
    };
    streamAPI.init(this.user, this.setUser)
    cqAPI.init(this.user, this.setUser)
  }

  onLogin(p){
    this.queue.push(p)
  }

  async login(u){
    await Promise.all(this.queue)
  }

  currentAuthenticatedUser() {
    return cqAPI.get('/isloggedin')
  }

  signIn({username, password, trustDevice, rememberMeToken}) {
    return cqAPI.post('/login',{
      username,
      password,
      referrerHost : location.host,
      trustDevice: trustDevice,
      rememberMeToken: rememberMeToken
    });
  }

  signOut() {
    return cqAPI.get('/logout').then((x)=>{
      this.storeUser(null);
      localStorage.removeItem("bid");
      localStorage.removeItem("business");
    })
  }

  /**
   * Initiate a password reset flow with the backend. If successful, the specified email will receive a password reset
   * link that a user must navigate to in order to complete the process.
   * @param email Email to initiate request for. Must belong to an existing user
   * @returns {Promise<Response>} Response object from API request.
   */
  requestPasswordReset(email) {
    return cqAPI.post('/forgot_password', { email });
  }
}

const Auth = new AUTH()

const loadPatients = async (inputValue, noFilter, currentBusinessOnly = false) => {
  try {
    const searchParams = new URLSearchParams({
      query:inputValue, 
      noFilter
    });
    currentBusinessOnly && searchParams.set("current_business_only", "1");
    const response = await cqAPI.get(
      `/findpatient?${searchParams.toString()}`
    );
    if (response.type === "ok") {
      return response.data
        .filter(
          (x) => noFilter || (x.adapters && x.adapters[x.bid])
        )
        .map((patient) => ({
          label: patient.profile.name,
          value: patient?.adapters && patient?.adapters[patient.bid],
          email: patient.email,
          dob : patient.profile.dob,
          _id : patient._id,
          bid: patient.bid,
        }));
    }
    return [];
  } catch (error) {
    console.error("Error searching patients:", error);
    return [];
  }
};

const loadProfiles = async (inputValue) => {
  try {
    const response = await cqAPI.get(
      `/a-dec/findProfiles?query=${inputValue}`
    );
    if (response.type === "ok") {
      return response.data
        .map((profile) => ({
          id: profile.id,
          name: profile.name,
          online: profile.online,
          adec_id: profile.adec_id,
          provider_id: profile.provider_id,
          profile_name: profile.profile_name
        }));
    }
  } catch (error) {
    console.error("Error searching profiles:", error);
    return [];
  }
}

const loadCustomers = async (inputValue) => {
  try {
    const response = await cqAPI.get(
      `/findLocation?query=${inputValue}`
    );
    if (response.type === "ok") {
      return response.data
        .map((location) => ({
          _id : location._id,
          name: location.name,
          created : location.created,
          start_date : location.start_date
        }));
    }
  } catch (error) {
    console.error("Error searching profiles:", error);
    return [];
  }
}

class LocationSelectorManager {
  static selectionPromise = null;
  static selectedLocation = null;

  static openLocationSelector = async () => {
    // If there's already a selected location, return it immediately.
    if (LocationSelectorManager.selectedLocation) {
      return Promise.resolve(LocationSelectorManager.selectedLocation);
    }

    // If a selection process is already ongoing, return the existing promise.
    if (LocationSelectorManager.selectionPromise) {
      return LocationSelectorManager.selectionPromise;
    }

    // Create a new promise for the selection process.
    LocationSelectorManager.selectionPromise = new Promise(async (resolve) => {
      const locations = await cqAPI.get('/locations?bid=').then(x => x.data);

      const handleLocationSelect = (event) => {
        // Find and set the selected location based on the event.
        LocationSelectorManager.selectedLocation = locations.find(location => location.bid === event.target.value);
      };

      const body = (
        <Select placeholder="Select location" onChange={handleLocationSelect}>
          {locations.map((location) => (
            <option key={location.bid} value={location.bid}>{location.name}</option>
          ))}
        </Select>
      );

      dialogManager.open({
        body: body,
        header: "Select a Location",
        acceptText: 'Select',
        onAccept: () => {
          // Ensure a location is selected before closing the dialog.
          if (!LocationSelectorManager.selectedLocation) {
            return false;
          }
          resolve(LocationSelectorManager.selectedLocation);
          return true
        },
        isClosable: false,
      });
    });

    return LocationSelectorManager.selectionPromise;
  };
}

const uploadString = async (content, fileType, name, _private=false) => {
  const formData = new FormData();
  const blob = new Blob([content], { type: fileType });
  formData.append("file", blob, encodeURIComponent(name));
  return cqAPI.post(`/upload${_private? '?private=1' : ''}`, formData);
};

const getUserForPatient = (bid, patient_guid)=>cqAPI.post(`/pp/user`,{to : `${bid}.${patient_guid}`}).then(response=>(response.type === 'ok') && response.data)

const getProviderForProfile = (adec_id)=>cqAPI.get(`/a-dec/getProvider`,{adec_id}).then(response=>(response.type === 'ok') && response.data.provider_id)

const getIfGroupedByReplyStatus = () => {
  const raw = localStorage.getItem("user");
  const user = raw ? JSON.parse(raw) : null;
  return user?.inbox_grouped_by_reply_status ?? false;
};

export { Auth, cqAPI, streamAPI, getBid, LocationSelectorManager, HOST, loadPatients, uploadString, getUid, getParam, getUserForPatient, loadProfiles, getProviderForProfile, loadCustomers, getIfGroupedByReplyStatus };
